import React from 'react';
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom';
import { useCart } from '../../state/cart/CartContext';
import { useAuth } from '../../state/auth/AuthProvider';
import { CartDrawer } from '../cart/CartDrawer';
import { ContactDetails } from './ContactDetails';
import { SocialLinks } from './SocialLinks';
import { CartDrawerProvider } from '../../state/ui/CartDrawerContext';

export function AppShell() {
  const cart = useCart();
  const auth = useAuth();
  const [isCartOpen, setIsCartOpen] = React.useState(false);
  const [navExpanded, setNavExpanded] = React.useState(false);
  const location = useLocation();

  React.useEffect(() => {
    setIsCartOpen(false);
    setNavExpanded(false);
  }, [location.pathname]);

  const drawerApi = React.useMemo(
    () => ({
      openCart: () => setIsCartOpen(true),
      closeCart: () => setIsCartOpen(false),
    }),
    []
  );
  const isAccountRoute =
    location.pathname === '/signin' || location.pathname === '/signup' || location.pathname === '/account';

  const closeNav = () => setNavExpanded(false);

  return (
    <div className="chunkzters-app-frame">
      <nav className="navbar navbar-expand-lg navbar-dark chunkzters-topbar">
        <div className="container chunkzters-container pt-4 pb-2 pb-lg-3 chunkzters-navbar-inner">
          <div className="chunkzters-navbar-controls">
            <Link
              to="/"
              className="navbar-brand d-flex align-items-center py-0 chunkzters-topbar-brand"
              onClick={closeNav}
            >
              <div className="chunkzters-logo">
                <picture>
                  <source srcSet={`${process.env.PUBLIC_URL}/assets/logo.webp`} type="image/webp" />
                  <img
                    src={`${process.env.PUBLIC_URL}/assets/logo-296.png`}
                    alt="Chunkzters"
                    width={148}
                    height={43}
                    decoding="async"
                  />
                </picture>
              </div>
            </Link>

            <button
              type="button"
              className="navbar-toggler chunkzters-navbar-toggler"
              aria-controls="chunkztersNavbar"
              aria-expanded={navExpanded}
              aria-label="Toggle menu"
              onClick={() => setNavExpanded((v) => !v)}
            >
              <span className="navbar-toggler-icon" />
            </button>
          </div>

          <SocialLinks className="chunkzters-navbar-social-center d-none d-lg-flex" />

          <div
            className={`collapse navbar-collapse chunkzters-navbar-collapse ${navExpanded ? 'show' : ''}`}
            id="chunkztersNavbar"
          >
            <SocialLinks className="chunkzters-navbar-social chunkzters-navbar-social--menu d-lg-none" />
            <ul className="navbar-nav ms-lg-auto flex-column flex-lg-row align-items-stretch align-items-lg-center gap-2 pt-2 pt-lg-0 mt-2 mt-lg-0 chunkzters-navbar-nav">
              <li className="nav-item w-100 w-lg-auto">
                <NavLink
                  to="/"
                  end
                  className={({ isActive }) =>
                    `btn btn-sm w-100 w-lg-auto d-inline-flex align-items-center justify-content-center flex-nowrap gap-1 chunkzters-navbar-btn ${isActive ? 'btn-light' : 'btn-outline-light'}`
                  }
                  aria-label="Menu"
                  onClick={closeNav}
                >
                  <i className="bi bi-grid flex-shrink-0" aria-hidden />
                  <span>Menu</span>
                </NavLink>
              </li>
              <li className="nav-item w-100 w-lg-auto">
                <button
                  type="button"
                  className="btn btn-sm btn-outline-light w-100 w-lg-auto position-relative d-inline-flex align-items-center justify-content-center flex-nowrap gap-1 chunkzters-navbar-btn"
                  onClick={() => {
                    closeNav();
                    setIsCartOpen((v) => !v);
                  }}
                  aria-label="Open cart"
                >
                  <i className="bi bi-bag flex-shrink-0" aria-hidden />
                  <span>Cart</span>
                  {cart.count > 0 ? (
                    <span
                      className="position-absolute top-0 start-100 translate-middle badge rounded-pill"
                      style={{ background: 'var(--chunkzters-red)', border: '2px solid var(--chunkzters-white)' }}
                    >
                      {cart.count}
                    </span>
                  ) : null}
                </button>
              </li>
              {auth.user ? (
                <>
                  <li className="nav-item w-100 w-lg-auto">
                    <NavLink
                      to="/account"
                      className={`btn btn-sm w-100 w-lg-auto d-inline-flex align-items-center justify-content-center flex-nowrap gap-1 chunkzters-navbar-btn ${isAccountRoute ? 'btn-light' : 'btn-outline-light'}`}
                      aria-label="My account"
                      onClick={closeNav}
                    >
                      <i className="bi bi-person-circle flex-shrink-0" aria-hidden />
                      <span>My Account</span>
                    </NavLink>
                  </li>
                  <li className="nav-item w-100 w-lg-auto">
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-light w-100 w-lg-auto d-inline-flex align-items-center justify-content-center flex-nowrap gap-1 chunkzters-navbar-btn"
                      aria-label="Sign out"
                      onClick={() => {
                        closeNav();
                        void auth.signOut();
                      }}
                    >
                      <i className="bi bi-box-arrow-right flex-shrink-0" aria-hidden />
                      <span>Sign out</span>
                    </button>
                  </li>
                </>
              ) : (
                <li className="nav-item w-100 w-lg-auto">
                  <NavLink
                    to="/account"
                    className={`btn btn-sm w-100 w-lg-auto d-inline-flex align-items-center justify-content-center flex-nowrap gap-1 chunkzters-navbar-btn ${isAccountRoute ? 'btn-light' : 'btn-outline-light'}`}
                    aria-label="Log in or sign up"
                    onClick={closeNav}
                  >
                    <i className="bi bi-person-circle flex-shrink-0" aria-hidden />
                    <span>Login</span>
                  </NavLink>
                </li>
              )}
            </ul>
          </div>
        </div>
      </nav>

      <CartDrawerProvider value={drawerApi}>
        <main className="flex-grow-1 chunkzters-main">
          <div className="chunkzters-main-sheet">
            <Outlet />
          </div>
          <footer className="chunkzters-footer">
            <div className="container chunkzters-container">
              <ContactDetails className="mb-3" inline />
              <div className="chunkzters-divider mb-3" />
              <div className="chunkzters-footer-bar d-flex flex-column flex-md-row align-items-md-center justify-content-between gap-2">
                <div className="chunkzters-footer-copy">
                  © {new Date().getFullYear()} Chunkzters
                </div>
                <SocialLinks />
                <nav className="d-flex gap-3 chunkzters-footer-legal" aria-label="Legal">
                  <Link to="/terms" className="chunkzters-footer-link">
                    Terms
                  </Link>
                  <Link to="/privacy" className="chunkzters-footer-link">
                    Privacy
                  </Link>
                  <Link to="/cookies" className="chunkzters-footer-link">
                    Cookies
                  </Link>
                </nav>
              </div>
            </div>
          </footer>
        </main>
        <CartDrawer isOpen={isCartOpen} onClose={() => setIsCartOpen(false)} />
      </CartDrawerProvider>
    </div>
  );
}

